import { useState } from "react";
import { Link } from "react-router-dom";
import "../PromoCodes.css";

const PromoForm = ({
    register,
    handleSubmit,
    errors,
    onSubmit,
    isLoading,
    isEdit = false,
    preview,
    handleImageChange,
    users = [],
    setValue,
    watch,
    trigger,
}) => {
    const [userSearch, setUserSearch] = useState("");
    const selectedUserIds = watch("users") || [];
    const discountType = watch("discountType");
    const promoCode = watch("promoCode");
    const discountValue = watch("discountValue");
    const minOrderValue = watch("minOrderValue");
    const maxDiscountCap = watch("maxDiscountCap");
    const startDate = watch("startDate");
    const expiryDate = watch("expiryDate");
    const status = watch("status");
    const firstOrderOnly = watch("firstOrderOnly");

    const toggleUser = (id) => {
        const current = [...selectedUserIds];
        const index = current.indexOf(id);
        if (index > -1) current.splice(index, 1);
        else current.push(id);
        setValue("users", current);
    };

    const generateCode = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let result = "";
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setValue("promoCode", result);
    };

    const activeUsers = users.filter((user) => user.active !== false);
    const filteredUsers = activeUsers.filter(
        (user) =>
            user.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
            user.lastName?.toLowerCase().includes(userSearch.toLowerCase()) ||
            user.email?.toLowerCase().includes(userSearch.toLowerCase())
    );

    const discountLabel =
        discountType === "fixed"
            ? `$${Number(discountValue || 0).toFixed(0)} OFF`
            : `${discountValue || 0}% OFF`;

    const SectionHead = ({ icon, iconClass, title, desc }) => (
        <div className="promo-section__head">
            <div className={`promo-section__icon ${iconClass}`}>
                <i className={`fas ${icon}`} />
            </div>
            <div>
                <h5 className="promo-section__title">{title}</h5>
                {desc && <p className="promo-section__desc">{desc}</p>}
            </div>
        </div>
    );

    const EmptyState = () => (
        <div className="py-5 text-center w-100">
            <i className="fas fa-search fs-2 text-muted opacity-25 mb-3 d-block" />
            <p className="text-muted fw-bold m-0">No matching users found</p>
            <p className="promo-field-hint">Try adjusting your search keywords</p>
        </div>
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="promo-form-layout">
                <div className="promo-form-main">
                    {/* BASIC INFO */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-tag"
                            iconClass="promo-section__icon--orange"
                            title="Basic Info"
                            desc="Promo code and banner image"
                        />
                        <div className="row g-4 align-items-start">
                            <div className="col-md-5">
                                <div
                                    className={`promo-banner-upload${preview ? " promo-banner-upload--has-preview" : ""}`}
                                    onClick={() => document.getElementById("promo-img-input").click()}
                                >
                                    {preview ? (
                                        <>
                                            <img src={preview} alt="Promo banner" />
                                            <div className="promo-banner-upload__overlay">
                                                <i className="fas fa-camera me-2" /> Change Banner
                                            </div>
                                        </>
                                    ) : (
                                        <div className="promo-banner-upload__placeholder">
                                            <div className="promo-banner-upload__icon">
                                                <i className="fas fa-image" />
                                            </div>
                                            <span className="promo-banner-upload__text">Upload Promo Banner</span>
                                            <span className="promo-banner-upload__hint">PNG, JPG — recommended 1200×400</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        id="promo-img-input"
                                        hidden
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>
                            <div className="col-md-7">
                                <label className="promo-field-label">
                                    Promo Code <span className="text-danger">*</span>
                                </label>
                                <div className="d-flex gap-2">
                                    <input
                                        type="text"
                                        className={`promo-input text-uppercase flex-grow-1 ${errors.promoCode ? "is-invalid" : ""}`}
                                        placeholder="SUMMER2026"
                                        {...register("promoCode")}
                                    />
                                    <button type="button" className="promo-btn-generate" onClick={generateCode}>
                                        <i className="fas fa-random me-1" /> Generate
                                    </button>
                                </div>
                                {errors.promoCode && (
                                    <span className="promo-field-error">{errors.promoCode.message}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* DISCOUNT */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-percent"
                            iconClass="promo-section__icon--blue"
                            title="Discount Settings"
                            desc="Choose discount type and value"
                        />
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="promo-field-label">
                                    Discount Type <span className="text-danger">*</span>
                                </label>
                                <select className="promo-input" {...register("discountType")}>
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount ($)</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="promo-field-label">
                                    Value <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative">
                                    <i
                                        className={`fas ${discountType === "percentage" ? "fa-percent" : "fa-dollar-sign"} promo-input-icon`}
                                    />
                                    <input
                                        type="number"
                                        className={`promo-input promo-input--icon ${errors.discountValue ? "is-invalid" : ""}`}
                                        placeholder={discountType === "percentage" ? "20" : "50"}
                                        {...register("discountValue")}
                                    />
                                </div>
                                {errors.discountValue && (
                                    <span className="promo-field-error">{errors.discountValue.message}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ORDER CONDITIONS */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-shopping-cart"
                            iconClass="promo-section__icon--green"
                            title="Order Conditions"
                            desc="Minimum order and discount cap"
                        />
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="promo-field-label">Min Order Value ($)</label>
                                <input type="number" className="promo-input" placeholder="e.g. 200" {...register("minOrderValue")} />
                            </div>
                            <div className="col-md-6">
                                <label className="promo-field-label">Max Discount Cap ($)</label>
                                <input type="number" className="promo-input" placeholder="e.g. 50" {...register("maxDiscountCap")} />
                            </div>
                        </div>
                    </div>

                    {/* TARGET AUDIENCE */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-users"
                            iconClass="promo-section__icon--purple"
                            title="Target Audience"
                            desc="Select users who can use this promo"
                        />
                        <div className="promo-audience-bar">
                            <div className="promo-audience-search">
                                <i className="fas fa-search" />
                                <input
                                    type="text"
                                    className="promo-input"
                                    placeholder="Search active users..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                            </div>
                            <div className="text-center">
                                <span className="promo-field-label d-block mb-0">Active</span>
                                <span className="fw-800 text-dark">{activeUsers.length}</span>
                            </div>
                            <div className="promo-selected-badge">
                                <div className="promo-selected-badge__inner">
                                    <span className="promo-selected-badge__label">Selected</span>
                                    <span className="promo-selected-badge__count">{selectedUserIds.length}</span>
                                </div>
                            </div>
                            <span
                                className="promo-select-all"
                                onClick={() => {
                                    if (selectedUserIds.length === filteredUsers.length) {
                                        setValue("users", []);
                                    } else {
                                        setValue("users", filteredUsers.map((u) => u._id));
                                    }
                                }}
                            >
                                {selectedUserIds.length === filteredUsers.length ? "Deselect All" : "Select All"}
                            </span>
                        </div>

                        <div className="promo-user-table-wrap d-none d-md-block">
                            <table className="promo-user-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 50 }} />
                                        <th>User</th>
                                        <th>Email</th>
                                        <th className="text-end">Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => {
                                            const isSelected = selectedUserIds.includes(user._id);
                                            const userName =
                                                `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
                                            return (
                                                <tr
                                                    key={user._id}
                                                    className={isSelected ? "is-selected" : ""}
                                                    onClick={() => toggleUser(user._id)}
                                                >
                                                    <td>
                                                        <div className={`promo-checkbox ${isSelected ? "is-checked" : ""}`}>
                                                            {isSelected && <i className="fas fa-check" />}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="promo-user-avatar">{userName.charAt(0).toUpperCase()}</div>
                                                            <div>
                                                                <span className={`fw-bold d-block ${isSelected ? "text-primary" : ""}`}>
                                                                    {userName}
                                                                </span>
                                                                <span className="promo-field-hint">
                                                                    ID: {user._id.slice(-6).toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="fw-semibold small">{user.email}</td>
                                                    <td className="text-end small fw-bold text-muted">
                                                        {new Date(user.createdAt).toLocaleDateString("en-US", {
                                                            month: "short",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        })}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={4}>
                                                <EmptyState />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-md-none">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => {
                                    const isSelected = selectedUserIds.includes(user._id);
                                    const userName =
                                        `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
                                    return (
                                        <div
                                            key={user._id}
                                            className={`promo-section mb-2 p-3 ${isSelected ? "border-warning" : ""}`}
                                            style={{ cursor: "pointer", padding: "0.85rem" }}
                                            onClick={() => toggleUser(user._id)}
                                        >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="promo-user-avatar">{userName.charAt(0).toUpperCase()}</div>
                                                    <div>
                                                        <span className="fw-bold d-block">{userName}</span>
                                                        <span className="promo-field-hint">{user.email}</span>
                                                    </div>
                                                </div>
                                                <div className={`promo-checkbox ${isSelected ? "is-checked" : ""}`}>
                                                    {isSelected && <i className="fas fa-check" />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <EmptyState />
                            )}
                        </div>
                    </div>

                    {/* USAGE RULES */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-sliders-h"
                            iconClass="promo-section__icon--slate"
                            title="Usage Rules"
                            desc="Limits and restrictions"
                        />
                        <div className="row g-3 align-items-stretch">
                            <div className="col-md-6">
                                <div className="promo-toggle-card h-100">
                                    <div>
                                        <p className="promo-toggle-card__title">First Order Only</p>
                                        <p className="promo-toggle-card__desc">Restricted to new users only</p>
                                    </div>
                                    <div className="promo-switch-wrap">
                                        <span className="promo-switch__label">{firstOrderOnly ? "ON" : "OFF"}</span>
                                        <label className="promo-switch">
                                            <input
                                                type="checkbox"
                                                {...register("firstOrderOnly")}
                                            />
                                            <span className="promo-switch__slider" />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="promo-field-label">Total Usage Limit (Global)</label>
                                <input
                                    type="number"
                                    min="1"
                                    className={`promo-input ${errors.usageLimit ? "is-invalid" : ""}`}
                                    placeholder="Unlimited"
                                    {...register("usageLimit")}
                                />
                                {errors.usageLimit && (
                                    <span className="promo-field-error">{errors.usageLimit.message}</span>
                                )}
                                <p className="promo-field-hint">Total uses across all users. Leave empty for unlimited.</p>
                            </div>
                            <div className="col-md-6">
                                <label className="promo-field-label">Uses Per User</label>
                                <input
                                    type="number"
                                    min="0"
                                    className={`promo-input ${errors.usageLimitPerUser ? "is-invalid" : ""}`}
                                    placeholder="1"
                                    {...register("usageLimitPerUser")}
                                />
                                {errors.usageLimitPerUser && (
                                    <span className="promo-field-error">{errors.usageLimitPerUser.message}</span>
                                )}
                                <p className="promo-field-hint">How many paid orders one user can use this code on. Empty = 1, 0 = unlimited.</p>
                            </div>
                        </div>
                    </div>

                    {/* SCHEDULE */}
                    <div className="promo-section">
                        <SectionHead
                            icon="fa-calendar-alt"
                            iconClass="promo-section__icon--orange"
                            title="Schedule & Status"
                            desc="Validity period and visibility"
                        />
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="promo-field-label">
                                    Start Date <span className="text-danger">*</span>
                                </label>
                                    <input
                                    type="date"
                                    className={`promo-input ${errors.startDate ? "is-invalid" : ""}`}
                                    {...register("startDate", {
                                        onChange: () => trigger?.("expiryDate"),
                                    })}
                                />
                                {errors.startDate && (
                                    <span className="promo-field-error">{errors.startDate.message}</span>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="promo-field-label">
                                    Expiry Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    min={startDate || undefined}
                                    className={`promo-input ${errors.expiryDate ? "is-invalid" : ""}`}
                                    {...register("expiryDate")}
                                />
                                {errors.expiryDate && (
                                    <span className="promo-field-error">{errors.expiryDate.message}</span>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="promo-field-label">
                                    Visibility Status <span className="text-danger">*</span>
                                </label>
                                <select className="promo-input" {...register("status")}>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* LIVE PREVIEW SIDEBAR */}
                <aside className="promo-preview-panel">
                    <div className="promo-preview-card">
                        <div className="promo-preview-card__banner">
                            {preview ? (
                                <img src={preview} alt="Preview" />
                            ) : (
                                <div className="promo-preview-card__banner-pattern" />
                            )}
                        </div>
                        <div className="promo-preview-card__body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="promo-field-label mb-0">Live Preview</span>
                                <span className={`promo-status-pill ${status === "true" ? "promo-status-pill--active" : "promo-status-pill--inactive"}`}>
                                    {status === "true" ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="promo-preview-card__code">
                                <i className="fas fa-ticket-alt" />
                                {promoCode || "PROMOCODE"}
                            </div>
                            <div className="promo-preview-card__discount">{discountLabel}</div>
                            <p className="promo-preview-card__meta mb-0">
                                {minOrderValue ? `Min order $${minOrderValue}` : "No minimum order"}
                                {maxDiscountCap ? ` · Max cap $${maxDiscountCap}` : ""}
                                {firstOrderOnly ? " · First order only" : ""}
                            </p>
                        </div>
                    </div>

                    <div className="promo-preview-stats">
                        <div className="promo-preview-stat">
                            <span className="promo-preview-stat__label">Target Users</span>
                            <span className="promo-preview-stat__value">{selectedUserIds.length}</span>
                        </div>
                        <div className="promo-preview-stat">
                            <span className="promo-preview-stat__label">Start Date</span>
                            <span className="promo-preview-stat__value">{startDate || "—"}</span>
                        </div>
                        <div className="promo-preview-stat">
                            <span className="promo-preview-stat__label">Expiry Date</span>
                            <span className="promo-preview-stat__value">{expiryDate || "—"}</span>
                        </div>
                        <div className="promo-preview-stat">
                            <span className="promo-preview-stat__label">Discount Type</span>
                            <span className="promo-preview-stat__value text-capitalize">{discountType || "—"}</span>
                        </div>
                    </div>
                </aside>
            </div>

            <div className="promo-form-footer">
                <button type="submit" className="promo-btn-save" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Saving...
                        </>
                    ) : isEdit ? (
                        "Update Promotion"
                    ) : (
                        "Save Promotion"
                    )}
                </button>
                <Link to="/promo-codes" className="promo-btn-cancel">
                    Cancel
                </Link>
            </div>
        </form>
    );
};

export default PromoForm;
